import React, { useEffect, useState } from 'react';

import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { BrokerUiModel } from '../../../shared/ui-models/broker.ui-model';
import { Delete, Edit, Folder, Mail, Phone, User } from '../../../ui/Icons';
import {
  addNewBroker,
  deleteBroker,
  getAllBrokers,
  updateBroker
} from '../../../shared/services/data/broker.data';
import { AddBrokerModal } from './components/add-broker-modal/add-broker-modal.component';
import { EditBrokerModal } from './components/edit-broker-modal/edit-broker-modal.component';
import { DeleteBrokerModal } from './components/delete-broker-modal/delete-broker-modal.component';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { birdToast } from '../../../shared/components/core-ui/toast';

export const BrokersListContainer = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [brokersList, setBrokersList] = useState<BrokerUiModel[]>([]);
  const [brokerToUpdate, setBrokerToUpdate] = useState<BrokerUiModel>();
  const [brokerToDelete, setBrokerToDelete] = useState<BrokerUiModel>();

  useEffect(() => {
    getAllBrokers().then((response: BrokerUiModel[] | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setBrokersList(response);
      }
    });
  }, []);

  const handleBrokerAdd = (broker: BrokerUiModel) => {
    const promise = addNewBroker(broker).then((response: BrokerUiModel | ErrorInfo) => {
      if (isErrorInfo(response)) {
        throw response;
      }
      setBrokersList([...brokersList, response]);
    });

    birdToast.promise(promise, {
      loadingMessage: 'Adding broker...',
      successMessage: 'Broker added successfully',
      errorPermissionMessage: 'You do not have permission to add broker',
      errorFailedMessage: 'Failed to add broker'
    });
  };

  const handleBrokerEdit = (brokerId: number, broker: Partial<BrokerUiModel>) => {
    const promise = updateBroker(brokerId, broker).then((response: BrokerUiModel | ErrorInfo) => {
      if (isErrorInfo(response)) {
        throw response;
      }
      setBrokersList(brokersList.map((item) => (item.id === response.id ? response : item)));
    });

    birdToast.promise(promise, {
      loadingMessage: 'Updating broker...',
      successMessage: 'Broker updated successfully',
      errorPermissionMessage: 'You do not have permission to update broker',
      errorFailedMessage: 'Failed to update broker'
    });
  };

  const handleBrokerDelete = (broker: BrokerUiModel) => {
    if (typeof broker.id !== 'number') return;
    const brokerId = broker.id;
    const promise = deleteBroker(brokerId).then((response: {} | ErrorInfo) => {
      if (isErrorInfo(response)) {
        throw response;
      }
      setBrokersList(brokersList.filter((item) => item.id !== brokerId));
    });

    birdToast.promise(promise, {
      loadingMessage: 'Deleting broker...',
      successMessage: 'Broker deleted successfully',
      errorPermissionMessage: 'You do not have permission to delete broker',
      errorFailedMessage: 'Failed to delete broker'
    });
  };

  return (
    <div className="flex h-full flex-col items-start justify-start md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <div className="flex w-full items-center justify-between gap-[1rem]">
        <div className="my-4 text-4xl font-recoleta font-bold text-primary-600">Brokers</div>
        <Button
          type={ButtonType.primary}
          onClick={() => {
            setShowAddModal(true);
          }}
        >
          Add Broker
        </Button>
      </div>
      {/*
        Says what a broker is for. This list drives the Broker picker that
        appears on the create-reservation form when the segment is BROKER, and
        an empty list there is otherwise a dead end with no explanation.
      */}
      <div className="mb-4 text-xl font-gilroy-medium text-neutral-500">
        Brokers bring us guests and are paid commission. They appear in the Broker list when
        creating a reservation with the BROKER segment.
      </div>
      <div className="grid h-full  w-full items-stretch gap-[1.6rem] pb-[2rem] [grid-template-columns:1fr] md:[grid-template-columns:repeat(auto-fill,minmax(320px,1fr))]">
        {brokersList.map((broker, index) => (
          <div
            key={broker.id ? broker.id : `broker-${index}`}
            className="group relative flex w-full flex-col justify-between overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
          >
            <div className="p-5">
              <div className="mb-3 flex items-start justify-between">
                <div className="flex gap-2 items-center ">
                  <Folder size="18px" />{' '}
                  <h3 className="truncate text-2xl font-bold text-neutral-500" title={broker.name}>
                    {broker.name || 'Untitled Broker'}
                  </h3>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    aria-label="Edit broker"
                    className="flex size-10 items-center justify-center rounded-full bg-white text-gray-400 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-primary-50 hover:text-primary-600 hover:ring-primary-200"
                    onClick={() => {
                      setBrokerToUpdate(broker);
                      setShowEditModal(true);
                    }}
                  >
                    <Edit size="16px" />
                  </button>
                  <button
                    type="button"
                    aria-label="Delete broker"
                    className="flex size-10 items-center justify-center rounded-full bg-white text-gray-400 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-rose-50 hover:text-rose-600 hover:ring-rose-200"
                    onClick={() => {
                      setBrokerToDelete(broker);
                      setShowDeleteModal(true);
                    }}
                  >
                    <Delete size="16px" />
                  </button>
                </div>
              </div>

              <div className="space-y-3 border-t border-gray-50 pt-3">
                <div className="flex items-center gap-2 text-xl text-neutral-500">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-400">
                    <User size="16px" />
                  </div>
                  <span className="truncate" title={broker.contactPersonName}>
                    {broker.contactPersonName || 'No Contact Person'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xl text-neutral-500">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-400">
                    <Mail size="16px" />
                  </div>
                  <span className="truncate" title={broker.contactPersonEmail}>
                    {broker.contactPersonEmail || 'No Email'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xl text-neutral-500">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-400">
                    <Phone size="16px" />
                  </div>
                  <span className="truncate">{broker.contactPersonPhoneNumber || '-'}</span>
                </div>
              </div>
            </div>
            <div className="h-1 w-full bg-gradient-to-r from-primary-50 via-primary-100 to-white opacity-50" />
          </div>
        ))}
      </div>

      {showAddModal && (
        <AddBrokerModal
          config={{ showModal: showAddModal }}
          eventHandlers={{
            cancelHandler: () => setShowAddModal(false),
            addBrokerHandler: (broker) => {
              handleBrokerAdd(broker);
              setShowAddModal(false);
            }
          }}
        />
      )}
      {showEditModal && (
        <EditBrokerModal
          data={{ broker: brokerToUpdate }}
          config={{ showModal: showEditModal }}
          eventHandlers={{
            cancelHandler: () => setShowEditModal(false),
            editBrokerHandler: (brokerId, broker) => {
              handleBrokerEdit(brokerId, broker);
              setShowEditModal(false);
            }
          }}
        />
      )}
      {showDeleteModal && (
        <DeleteBrokerModal
          data={{ broker: brokerToDelete }}
          config={{ showModal: showDeleteModal }}
          eventHandlers={{
            cancelHandler: () => setShowDeleteModal(false),
            deleteBrokerHandler: (broker) => {
              handleBrokerDelete(broker);
              setShowDeleteModal(false);
            }
          }}
        />
      )}
    </div>
  );
};
